import axios from "axios";
import { getToken } from "Shared/baseURL";
import { attachApiInterceptors } from "Shared/apiInterceptors";

const mediaApiBase = `${String(import.meta.env.VITE_BASEURL || "/api").replace(/\/$/, "")}/ms/v1/media`;
const mediaClient = axios.create({ baseURL: mediaApiBase });
attachApiInterceptors(mediaClient, "mediaClient:Utils/mediaUpload");
const MEDIA_URL_CACHE_LIMIT = 500;
const MEDIA_URL_CACHE_SAFETY_MS = 60 * 1000;
const mediaUrlCache = new Map();
const mediaUrlRequests = new Map();

const normalizeMediaId = (mediaId) => String(mediaId || "").trim();

const normalizeMediaTtl = (ttl) => {
  const parsed = Number(ttl);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 15;
};

const getMediaUrlCacheKey = (mediaId, ttl) =>
  `${normalizeMediaId(mediaId)}:${normalizeMediaTtl(ttl)}`;

const getMediaUrlCacheExpiry = (ttl) => {
  const ttlMs = normalizeMediaTtl(ttl) * 1000;
  const safetyMs = Math.min(
    MEDIA_URL_CACHE_SAFETY_MS,
    Math.max(1000, Math.floor(ttlMs * 0.1)),
  );
  return Date.now() + Math.max(1000, ttlMs - safetyMs);
};

const readCachedMediaUrl = (mediaId, ttl) => {
  const cacheKey = getMediaUrlCacheKey(mediaId, ttl);
  const cached = mediaUrlCache.get(cacheKey);

  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    mediaUrlCache.delete(cacheKey);
    return null;
  }

  return cached.url;
};

const writeCachedMediaUrl = (mediaId, ttl, url) => {
  const normalizedId = normalizeMediaId(mediaId);
  const normalizedUrl = typeof url === "string" ? url.trim() : "";

  if (!normalizedId || !normalizedUrl) return;

  const cacheKey = getMediaUrlCacheKey(normalizedId, ttl);
  mediaUrlCache.delete(cacheKey);
  mediaUrlCache.set(cacheKey, {
    url: normalizedUrl,
    expiresAt: getMediaUrlCacheExpiry(ttl),
  });

  while (mediaUrlCache.size > MEDIA_URL_CACHE_LIMIT) {
    const oldestKey = mediaUrlCache.keys().next().value;
    if (!oldestKey) break;
    mediaUrlCache.delete(oldestKey);
  }
};

const normalizeBearerToken = (token) => {
  if (!token) return null;
  const raw = String(token).trim();
  if (!raw) return null;
  return /^Bearer\s+/i.test(raw) ? raw : `Bearer ${raw}`;
};

export const generateIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const resolveAuthHeader = async (token) => {
  const resolvedToken = token ?? (await getToken());
  const bearer = normalizeBearerToken(resolvedToken);
  if (!bearer) {
    throw new Error("Authentication required");
  }
  return {
    Authorization: bearer,
  };
};

export const createMediaUpload = async ({
  file,
  mediaType = "POST_MEDIA",
  token,
  idempotencyKey = generateIdempotencyKey(),
}) => {
  if (!file) {
    throw new Error("No file provided");
  }

  const headers = await resolveAuthHeader(token);
  const response = await mediaClient.post(
    "/uploads",
    {
      idempotencyKey,
      mimeType: file.type || "application/octet-stream",
      mediaType,
    },
    {
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
    },
  );

  const mediaId = response?.data?.id;
  const preSignedUrl = response?.data?.preSignedUrl;

  if (!mediaId || !preSignedUrl) {
    throw new Error("Media upload initialization failed.");
  }

  return {
    mediaId: String(mediaId),
    preSignedUrl: String(preSignedUrl),
    idempotencyKey,
  };
};

const extractMediaUploadResponse = (payload) => {
  if (!payload) {
    return { mediaId: null, mediaUrl: null, preSignedUrl: null };
  }

  if (typeof payload === "string") {
    return {
      mediaId: null,
      mediaUrl: payload.trim() || null,
      preSignedUrl: null,
    };
  }

  if (typeof payload !== "object") {
    return { mediaId: null, mediaUrl: null, preSignedUrl: null };
  }

  const mediaId = String(
    payload.id || payload.mediaId || payload.mediaID || payload.uuid || "",
  ).trim();

  const mediaUrl = String(
    payload.s3Url ||
      payload.url ||
      payload.mediaUrl ||
      payload.fileUrl ||
      payload.path ||
      payload.location ||
      payload.downloadUrl ||
      payload.publicUrl ||
      payload.preSignedUrl ||
      "",
  ).trim();

  const preSignedUrl = String(payload.preSignedUrl || "").trim();

  return {
    mediaId: mediaId || null,
    mediaUrl: mediaUrl || null,
    preSignedUrl: preSignedUrl || null,
  };
};

const uploadMediaViaBackend = async ({
  file,
  mediaType = "POST_MEDIA",
  token,
  idempotencyKey = generateIdempotencyKey(),
}) => {
  if (!file) {
    throw new Error("No file provided");
  }

  const headers = await resolveAuthHeader(token);
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mediaType", mediaType);
  formData.append("mimeType", file.type || "application/octet-stream");
  formData.append("idempotencyKey", idempotencyKey);

  const response = await mediaClient.post("/uploads", formData, {
    headers,
  });

  const parsed = extractMediaUploadResponse(response?.data);
  if (!parsed.mediaId && !parsed.mediaUrl && !parsed.preSignedUrl) {
    throw new Error("Media upload returned an empty response.");
  }

  return {
    mediaId: parsed.mediaId,
    mediaUrl: parsed.mediaUrl,
    preSignedUrl: parsed.preSignedUrl,
    idempotencyKey,
  };
};

export const uploadToPresignedUrl = async ({
  preSignedUrl,
  file,
  idempotencyKey,
}) => {
  if (!preSignedUrl || !file) {
    throw new Error("presigned URL and file are required");
  }

  const headers = {
    "Content-Type": file.type || "application/octet-stream",
  };
  if (idempotencyKey) {
    headers["x-amz-meta-idempotencykey"] = idempotencyKey;
  }

  const uploadResponse = await fetch(preSignedUrl, {
    method: "PUT",
    headers,
    body: file,
  });

  if (!uploadResponse.ok) {
    const responseText = await uploadResponse.text().catch(() => "");
    throw new Error(
      `Media upload failed (${uploadResponse.status}): ${responseText || "Unknown error"}`,
    );
  }

  return true;
};

export const resolveMediaUrl = async ({ mediaId, token, ttl = 15 }) => {
  const normalizedMediaId = normalizeMediaId(mediaId);
  if (!normalizedMediaId) {
    return null;
  }

  const cachedUrl = readCachedMediaUrl(normalizedMediaId, ttl);
  if (cachedUrl) {
    return cachedUrl;
  }

  const requestKey = getMediaUrlCacheKey(normalizedMediaId, ttl);
  const pendingRequest = mediaUrlRequests.get(requestKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    const headers = await resolveAuthHeader(token);
    const response = await mediaClient.get(
      `/${encodeURIComponent(normalizedMediaId)}`,
      {
        params: { ttl },
        headers,
      },
    );

    const payload = response?.data;
    if (typeof payload === "string") {
      const resolvedUrl = payload.trim() || null;
      writeCachedMediaUrl(normalizedMediaId, ttl, resolvedUrl);
      return resolvedUrl;
    }

    if (payload && typeof payload === "object") {
      const resolvedUrl =
        payload.preSignedUrl ||
        payload.downloadUrl ||
        payload.fileUrl ||
        payload.s3Url ||
        payload.mediaUrl ||
        payload.location ||
        payload.path ||
        payload.url ||
        null;

      writeCachedMediaUrl(normalizedMediaId, ttl, resolvedUrl);
      return resolvedUrl;
    }

    return null;
  })().finally(() => {
    mediaUrlRequests.delete(requestKey);
  });

  mediaUrlRequests.set(requestKey, request);
  return request;
};

export const resolveMediaUrlsBulk = async ({
  mediaIds = [],
  token,
  ttl = 15,
}) => {
  const validIds = Array.isArray(mediaIds)
    ? Array.from(
        new Set(mediaIds.map((id) => String(id || "").trim()).filter(Boolean)),
      )
    : [];

  if (!validIds.length) {
    return {};
  }

  const cachedUrls = {};
  const missingIds = [];

  validIds.forEach((mediaId) => {
    const cachedUrl = readCachedMediaUrl(mediaId, ttl);
    if (cachedUrl) {
      cachedUrls[mediaId] = cachedUrl;
    } else {
      missingIds.push(mediaId);
    }
  });

  if (!missingIds.length) {
    return cachedUrls;
  }

  const headers = await resolveAuthHeader(token);
  const response = await mediaClient.get("/bulk", {
    params: {
      ids: missingIds,
      ttl,
    },
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams();
      const ids = Array.isArray(params?.ids) ? params.ids : [];

      ids.forEach((id) => {
        searchParams.append("ids", String(id));
      });

      if (params?.ttl != null) {
        searchParams.append("ttl", String(params.ttl));
      }

      return searchParams.toString();
    },
    headers,
  });

  const payload = response?.data;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return cachedUrls;
  }

  return Object.entries(payload).reduce((acc, [id, url]) => {
    const mediaId = String(id || "").trim();
    const mediaUrl = typeof url === "string" ? url.trim() : "";

    if (mediaId && mediaUrl) {
      acc[mediaId] = mediaUrl;
      writeCachedMediaUrl(mediaId, ttl, mediaUrl);
    }

    return acc;
  }, cachedUrls);
};

export const confirmMediaUpload = async ({ mediaIds, token }) => {
  const validIds = Array.isArray(mediaIds)
    ? mediaIds.map((id) => String(id || "").trim()).filter(Boolean)
    : [];
  if (!validIds.length) {
    return null;
  }

  const headers = await resolveAuthHeader(token);
  const response = await mediaClient.post("/bulk/confirm", validIds, {
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
  });

  return response?.data || null;
};

export const uploadMediaFile = async ({
  file,
  mediaType = "POST_MEDIA",
  token,
  resolveUrlAfterUpload = true,
  confirmAfterUpload = false,
}) => {
  if (!file) {
    throw new Error("No file provided");
  }

  const idempotencyKey = generateIdempotencyKey();
  let mediaId = null;
  let mediaUrl = null;
  let preSignedUrl = null;

  try {
    const initializedUpload = await createMediaUpload({
      file,
      mediaType,
      token,
      idempotencyKey,
    });

    mediaId = initializedUpload.mediaId;
    preSignedUrl = initializedUpload.preSignedUrl;

    await uploadToPresignedUrl({
      preSignedUrl,
      file,
      idempotencyKey,
    });
  } catch (initializeUploadError) {
    const status = initializeUploadError?.response?.status;
    const shouldFallbackToLegacy =
      [400, 404, 405, 415, 422].includes(status) || !status;

    if (!shouldFallbackToLegacy) {
      throw initializeUploadError;
    }

    const legacyUpload = await uploadMediaViaBackend({
      file,
      mediaType,
      token,
      idempotencyKey,
    });

    mediaId = legacyUpload.mediaId;
    mediaUrl = legacyUpload.mediaUrl;
    preSignedUrl = legacyUpload.preSignedUrl;
  }

  if (confirmAfterUpload && mediaId) {
    try {
      await confirmMediaUpload({ mediaIds: [mediaId], token });
    } catch (confirmError) {
      if (import.meta.env.DEV) {
        console.warn("[mediaUpload] confirm failed", confirmError);
      }
      throw new Error(
        confirmError?.response?.data?.message ||
          confirmError?.message ||
          "Media confirmation failed.",
      );
    }
  }

  const isMediaEndpointUrl = (value) =>
    typeof value === "string" && /\/ms\/v1\/media\/[^/?#]+/i.test(value);

  const isUnsignedS3Url = (value) => {
    if (typeof value !== "string") return false;
    try {
      const parsed = new URL(value);
      const isS3 =
        parsed.hostname.includes(".s3.") &&
        parsed.hostname.includes("amazonaws.com");
      const hasSignature =
        parsed.searchParams.has("X-Amz-Signature") ||
        parsed.searchParams.has("X-Amz-Algorithm");
      return isS3 && !hasSignature;
    } catch {
      return false;
    }
  };

  let resolvedMediaUrl =
    (typeof mediaUrl === "string" ? mediaUrl.trim() : "") ||
    (typeof preSignedUrl === "string" ? preSignedUrl.split("?")[0] : "");

  if (resolveUrlAfterUpload) {
    const needsResolve =
      mediaId &&
      (!resolvedMediaUrl ||
        isMediaEndpointUrl(resolvedMediaUrl) ||
        isUnsignedS3Url(resolvedMediaUrl));
    if (needsResolve) {
      const RESOLVE_MAX_ATTEMPTS = 3;
      const RESOLVE_BASE_DELAY_MS = 800;
      for (let attempt = 1; attempt <= RESOLVE_MAX_ATTEMPTS; attempt++) {
        try {
          const resolved = await resolveMediaUrl({ mediaId, token });
          if (resolved) {
            resolvedMediaUrl = resolved;
            break;
          }
        } catch (error) {
          if (attempt < RESOLVE_MAX_ATTEMPTS) {
            await new Promise((r) =>
              setTimeout(r, RESOLVE_BASE_DELAY_MS * attempt),
            );
          } else if (import.meta.env.DEV) {
            console.warn("[mediaUpload] resolve url failed", error);
          }
        }
      }
    }
  }

  return {
    mediaId,
    mediaUrl: resolvedMediaUrl || null,
    fileName: file?.name || "",
    preSignedUrl: preSignedUrl || null,
    idempotencyKey,
  };
};

export const uploadMediaFiles = async ({
  files = [],
  mediaType = "POST_MEDIA",
  token,
  resolveUrlAfterUpload = true,
  confirmAfterUpload = false,
}) => {
  const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!normalizedFiles.length) {
    return [];
  }

  const uploaded = await Promise.all(
    normalizedFiles.map((file) =>
      uploadMediaFile({
        file,
        mediaType,
        token,
        resolveUrlAfterUpload,
        confirmAfterUpload,
      }),
    ),
  );

  return uploaded;
};
